<template>
  <PageShell
    title="LotLedger Login"
    subtitle="Secure sign-in with email + 2FA"
    :wide="false"
    :hideFooter="true"
  >
    <template #actions>
      <router-link class="ll-pill ghost" to="/">Home</router-link>
    </template>

    <div class="auth-wrap">
      <div class="auth-card">
        <!-- Step 1 -->
        <div v-if="step === 'login'">
          <div class="auth-title">Sign in</div>
          <div class="auth-sub">Enter your email and password.</div>

          <input type="email" v-model="email" class="input" placeholder="Email" />
          <input type="password" v-model="password" class="input" placeholder="Password" />

          <button class="ll-pill full" type="button" @click="startLogin">Login</button>
        </div>

        <!-- Step 2 -->
        <div v-else-if="step === '2fa'">
          <div class="auth-title">Check your email</div>
          <div class="auth-sub">Enter the 6-digit code we sent you.</div>

          <input v-model="code" class="input" placeholder="6-digit code" />

          <button class="ll-pill full" type="button" @click="verify2FA">Verify</button>
          <button class="ll-pill ghost full" type="button" @click="resend2FA">Resend Code</button>
        </div>

        <!-- Step 3 -->
        <div v-else>
          <div class="auth-title">✅ Logged in</div>
          <div class="auth-sub">Login successful. Continue to LotLedger.</div>

          <button class="ll-pill full" type="button" @click="goToHome">Go to LotLedger</button>
        </div>

        <div v-if="errorMessage" class="error">{{ errorMessage }}</div>
      </div>
    </div>
  </PageShell>
</template>

<script>
import PageShell from "@/components/layout/PageShell.vue";
import api from "@/services/api";
import { useRouter } from "vue-router";

export default {
  components: { PageShell },

  data() {
    return {
      email: "",
      password: "",
      code: "",
      step: "login",
      errorMessage: "",
    };
  },

  setup() {
    const router = useRouter();
    return { router };
  },

  async mounted() {
    const token = localStorage.getItem("lotledgerToken");
    if (token) {
      try {
        await api.get("/auction-houses/available");
        this.step = "done";
      } catch (e) {
        localStorage.removeItem("lotledgerToken");
        this.step = "login";
      }
    }
  },

  methods: {
    async startLogin() {
      this.errorMessage = "";

      if (!this.email || !this.password) {
        this.errorMessage = "Email and password are required.";
        return;
      }

      try {
        const r = await api.post("/auth/login", {
          email: this.email,
          password: this.password,
        });

        if (r.data?.message) {
          this.step = "2fa";
          return;
        }

        this.step = "2fa";
      } catch (err) {
        this.errorMessage =
          err.response?.data?.error || "Login failed. Check your email/password.";
      }
    },

    async resend2FA() {
      await this.startLogin();
    },

    async verify2FA() {
      this.errorMessage = "";

      if (!this.code) {
        this.errorMessage = "Enter the 6-digit code that was emailed to you.";
        return;
      }

      try {
        const r = await api.post("/auth/login-2fa", {
          email: this.email,
          code: this.code,
        });

        const token = r.data?.token;
        if (!token) {
          this.errorMessage = "No token returned. Try again.";
          return;
        }

        localStorage.setItem("lotledgerToken", token);
        this.step = "done";
      } catch (err) {
        this.errorMessage =
          err.response?.data?.error || "Invalid or expired code. Try again.";
      }
    },

    goToHome() {
      const token = localStorage.getItem("lotledgerToken");
      if (!token) {
        this.errorMessage = "You're not authenticated yet. Please re-verify 2FA.";
        this.step = "login";
        return;
      }

      this.$router.push("/");
    },
  },
};
</script>

<style scoped>
.auth-wrap {
  display: grid;
  place-items: center;
  min-height: 55vh;
}

.auth-card {
  width: 100%;
  max-width: 420px;
  border-radius: 16px;
  border: 1px solid rgba(131, 167, 203, 0.85);
  background: rgba(255, 255, 255, 0.92);
  box-shadow: 0px 16px 40px rgba(0, 0, 0, 0.12);
  padding: 14px;
  display: grid;
  gap: 10px;
}

.auth-title {
  font-weight: 900;
  font-size: 18px;
}

.auth-sub {
  font-size: 12.5px;
  opacity: 0.78;
  font-weight: 650;
  line-height: 1.25rem;
}

.input {
  padding: 10px 12px;
  border-radius: 12px;
  border: 1px solid rgba(176, 196, 222, 0.9);
  background: #f6fbff;
  font-weight: 650;
  outline: none;
}

.input:focus {
  border-color: rgba(50, 123, 168, 0.55);
}

.ll-pill.full {
  width: 100%;
}

.error {
  color: #b00020;
  font-weight: 800;
  font-size: 12.5px;
  padding-top: 4px;
}
</style>
